import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceStats = ({ stats, className = '' }) => {
  const statItems = [
    {
      key: 'received',
      label: 'Received',
      value: stats?.received,
      icon: 'Inbox',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      key: 'accepted',
      label: 'Accepted',
      value: stats?.accepted,
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      key: 'inDiscussion',
      label: 'In Discussion',
      value: stats?.inDiscussion,
      icon: 'MessageCircle',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      key: 'booked',
      label: 'Booked',
      value: stats?.booked,
      icon: 'Calendar',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    }
  ];

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const calculateConversionRate = () => {
    if (stats?.received === 0) return 0;
    return Math.round((stats?.accepted / stats?.received) * 100);
  };

  const calculateSuccessRate = () => {
    if (stats?.accepted === 0) return 0;
    return Math.round((stats?.booked / stats?.accepted) * 100);
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Performance Overview</h2>
        <div className="flex items-center space-x-1 text-sm text-muted-foreground">
          <Icon name="TrendingUp" size={16} />
          <span>This month</span>
        </div>
      </div>
      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {statItems?.map((item) => (
          <div key={item?.key} className="text-center">
            <div className={`w-12 h-12 ${item?.bgColor} rounded-lg flex items-center justify-center mx-auto mb-2`}>
              <Icon name={item?.icon} size={20} className={item?.color} />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{item?.value}</div>
            <div className="text-sm text-muted-foreground">{item?.label}</div>
          </div>
        ))}
      </div>
      {/* Revenue Section */}
      <div className="border-t border-border pt-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">Total Revenue</span>
          <span className="text-lg font-semibold text-foreground">
            {formatCurrency(stats?.totalRevenue)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Acceptance Fees</span>
          <span className="text-sm text-foreground">
            {formatCurrency(stats?.accepted * 5)}
          </span>
        </div>
      </div>
      {/* Performance Metrics */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Conversion Rate</span>
            <span className="text-sm font-medium text-foreground">{calculateConversionRate()}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(calculateConversionRate(), 100)}%` }}
            ></div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Success Rate</span>
            <span className="text-sm font-medium text-foreground">{calculateSuccessRate()}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-success h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(calculateSuccessRate(), 100)}%` }}
            ></div>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="border-t border-border pt-4 mt-4">
        <div className="grid grid-cols-2 gap-2">
          <button 
            onClick={() => window.location.href = '/agent-profile'}
            className="flex items-center justify-center space-x-2 p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="Settings" size={14} />
            <span>Settings</span>
          </button>
          <button 
            onClick={() => window.location.href = '/messages'}
            className="flex items-center justify-center space-x-2 p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="MessageCircle" size={14} />
            <span>Messages</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PerformanceStats;